import React from 'react';
import styled from 'styled-components';

/* 공통 섹션 래퍼 */
export const InfoSection = ({ title, subtitle, children }) => (
  <SectionWrapper>
    {(title || subtitle) && (
      <Header>
        {title && <h2>{title}</h2>}
        {subtitle && <p>{subtitle}</p>}
      </Header>
    )}
    {children}
  </SectionWrapper>
);

const SectionWrapper = styled.section`
  padding: 80px 5% 80px;
  max-width: 1400px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 60px 5% 60px;
  }
`;

const Header = styled.div`
  margin-bottom: 50px;
  h2 {
    font-size: 35px; font-weight: 800; letter-spacing: -0.5px;
    color: #0e0c31ff; margin: 12px 0 8px; text-align: center;
  }
  p {
    color: #6b7280; margin: 0; white-space: pre-line; text-align: center;
  }
`;

export const SimpleDivider = styled.hr`
  width: 100%; height: 1px; background: #e5e7eb; border: none; margin: 40px 0;
`;

/* 1) 사양표 */
export const SpecTable = ({ rows }) => (
  <Table>
    <tbody>
      {rows.map((r, i) => (
        <tr key={i}>
          <th>{r.label}</th>
          <td>{r.value}</td>
        </tr>
      ))}
    </tbody>
  </Table>
);
const Table = styled.table`
  width: 100%; border-collapse: collapse; margin-bottom: 32px; table-layout: fixed;
  th, td { padding: 12px 14px; text-align: left; vertical-align: middle; word-break: keep-all; border: 1px solid #eef1f5; }
  th { width: 22%; background: #f7f7f7; font-weight: 700; }
`;

/* 2) 납품실적 */
export const SupplyTable = ({ years, rows, aggregation, noteText }) => {
  const autoNote =
    aggregation === 'count' ? '집계기준: 판매수량(개)'
    : aggregation === 'amount' ? '집계기준: 납품액(천만 원)'
    : null;
  const note = noteText ?? autoNote;

  return (
    <TableWrap>
      {note && (
        <TopBar>
          <NoteBadge>{note}</NoteBadge>
        </TopBar>
      )}
      <ScrollX>
        <StyledSupplyTable>
          <thead>
            <tr>
              <th>기관/년도</th>
              {years.map((y) => {
                const isTotalCol = typeof y === 'string' && y.includes('합계');
                return <th key={y} className={isTotalCol ? 'total-col' : ''}>{y}</th>;
              })}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const isTotalRow = typeof r.org === 'string' && r.org.includes('합계');
              return (
                <tr key={i} className={isTotalRow ? 'total-row' : ''}>
                  <th className="org">{r.org}</th>
                  {years.map((y) => {
                    const isTotalCol = typeof y === 'string' && y.includes('합계');
                    return <td key={y} className={isTotalCol ? 'total-col' : ''}>{r[y] ?? ''}</td>;
                  })}
                </tr>
              );
            })}
          </tbody>
        </StyledSupplyTable>
      </ScrollX>
    </TableWrap>
  );
};
/* styles */
const TableWrap = styled.div` width: 100%; margin-bottom: 12px; `;
const TopBar = styled.div`
  display: flex; justify-content: flex-end; align-items: center; margin-bottom: 8px;
  @media (max-width: 768px) { justify-content: flex-end; }
`;
const NoteBadge = styled.span` display: inline-block; font-size: 13px; color: #374151; padding: 6px 10px; white-space: nowrap; `;
const ScrollX = styled.div` overflow-x: auto; `;

const StyledSupplyTable = styled.table`
  width: 100%; border-collapse: collapse; margin-bottom: 0;
  thead th {
    background: #f7f7f7; border: 1px solid #eef1f5; padding: 10px; font-weight: 700; white-space: nowrap;
  }
  tbody th.org {
    text-align: left; background: #f7f7f7; font-weight: 700; padding: 10px 12px; border: 1px solid #eef1f5; min-width: 160px;
  }
  td { text-align: center; border: 1px solid #eef1f5; padding: 10px 8px; min-width: 70px; }
  tbody tr.total-row td, td.total-col { font-weight: 700; color: #005BAC; }

  /* ✅ 모바일: 기관(첫 열) sticky 고정 */
  @media (max-width: 768px) {
    border-spacing: 0;
    tbody th.org {
      position: sticky;
      left: 0;
      z-index: 1;
      box-shadow: 8px 0 12px -8px rgba(0,0,0,0.06);
    }
  }
`;

/* 공용 이미지 박스 */
const ImgBox = styled.div`
  width: 100%; 
  background: #fff;
  display: flex; align-items: center; justify-content: center; overflow: hidden;
  img { max-width: 100%; max-height: 100%; width: auto; height: auto; object-fit: contain; display: block; }
  @media (max-width: 1024px) { height: 360px; }
  @media (max-width: 768px)  { height: 300px; }
`;
const ImgBoxTop = styled(ImgBox)` align-items: flex-start; `;
const ImgBoxAuto = styled.div`
  width: 100%; background: #fff; display: flex; align-items: flex-start; justify-content: center; overflow: hidden;
  img { max-width: 100%; height: auto; max-height: 420px; object-fit: contain; display: block; }
`;
const ImgBoxSm = styled(ImgBox)`
  height: 300px;
  @media (max-width: 1024px) { height: 260px; }
  @media (max-width: 768px)  { height: 220px; }
`;



/* 3) 활용범위/갤러리 */
export const UsageGrid = ({ items, columns, autoHeight = false }) => {
  const Box = autoHeight ? ImgBoxAuto : ImgBox;  // ✅ 기존 ImgBoxAuto 재사용

  return (
    <Grid cols={columns}>
      {items.map((it, i) => (
        <Card key={i}>
          <Box>
            <img
              src={it.image}
              alt={it.caption}
              loading="lazy"
              onError={(e) => (e.currentTarget.style.display = 'none')}
            />
          </Box>
          {it.caption && <Caption>{it.caption}</Caption>}
        </Card>
      ))}
    </Grid>
  );
};

const Grid = styled.div`
  display: grid;
  grid-template-columns: ${({ cols }) => cols ? `repeat(${cols}, 1fr)` : 'repeat(4, 1fr)'};
  gap: 24px;
  padding-bottom: 28px;
  @media (max-width: 1024px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 520px)  { grid-template-columns: 1fr; }
`;
const Card = styled.div` background: #fff; `;
const Caption = styled.div` padding: 12px; text-align: center; font-size: 15px; font-weight: 600; color: #111; `;

/* 4) 제품 유형 */
const TypeRow = styled.div` margin-bottom: 36px; `;
const TypeTitle = styled.h4` font-size: 25px; font-weight: 700; margin: 0; color: #005BAC; `;
const TypeSubtitle = styled.p` margin-bottom: 12px; font-size: 18px; color: #6b7280; white-space: pre-line; `;
const GroupHead = styled.div` display: flex; flex-direction: column; row-gap: 8px; margin-bottom: 16px; `;
const TypeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2rem;
  justify-items: center;
`;

/* ✅ 제품 구성도용 전용 박스 & 래퍼 */
const TypeItemWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const TypeItemLabel = styled.div`
  font-size: 15px;
  font-weight: 600;
  margin-top: 8px;
  text-align: center;
  color: #444;
`;

/* ✅ TypeBlock에서만 사용할 고정 높이 이미지 박스 */
const TypeImageBox = styled(ImgBox)`
  height: 320px;

  @media (max-width: 1024px) {
    height: 280px;
  }
  @media (max-width: 768px) {
    height: 240px;
  }
`;

export const TypeBlock = ({ groups, compact = false }) => (
  <div>
    {groups.map((g, gi) => (
      <TypeRow key={gi}>
        <GroupHead>
          <TypeTitle>{g.title}</TypeTitle>
          {g.subtitle && <TypeSubtitle>{g.subtitle}</TypeSubtitle>}
        </GroupHead>

        <TypeGrid>
          {g.items.map((item, ii) => {
            // compact면 기존처럼 자동 높이, 아니면 고정 높이 박스 사용
            const Box = compact ? ImgBoxAuto : TypeImageBox;

            return (
              <TypeItemWrapper key={ii}>
                <Box>
                  <img
                    src={item.src || item.image}
                    alt={item.label || g.title}
                    loading="lazy"
                    onError={(e) => (e.currentTarget.style.display = 'none')}
                  />
                </Box>

                {item.label && (
                  <TypeItemLabel>{item.label}</TypeItemLabel>
                )}
              </TypeItemWrapper>
            );
          })}
        </TypeGrid>
      </TypeRow>
    ))}
  </div>
);

/* 5) 사용방법(스텝) */
const Steps = styled.ol`
  list-style: none; padding: 0; margin: 0;
  display: grid; grid-template-columns: repeat(2, 1fr); gap: 18px;
  @media (max-width: 700px) { grid-template-columns: 1fr; }
`;
const StepCard = styled.li` background: #fff; padding: 14px; `;
const StepCaption = styled.div`
  margin-top: 10px; font-size: 15px; font-weight: 600; color: #111; line-height: 1.5; text-align: center; white-space: pre-line;
`;
export const UsageSteps = ({ steps }) => (
  <Steps>
    {steps.map((s, i) => (
      <StepCard key={i}>
        <ImgBoxSm>
          <img src={s.image} alt={`Step ${i + 1}`} loading="lazy" onError={(e) => (e.currentTarget.style.display = 'none')} />
        </ImgBoxSm>
        <StepCaption>{s.text}</StepCaption>
      </StepCard>
    ))}
  </Steps>
);

/* 6) 구성도 */
const PartsGridEl = styled.div`
  display: grid; grid-template-columns: repeat(2, 1fr); gap: 18px; justify-items: center;
  @media (max-width: 700px) { grid-template-columns: 1fr; }
`;
const PartItem = styled.div` width: 100%; background: #fff; border: 1px solid #eef1f5; `;
const PartLabel = styled.div` padding: 12px; text-align: center; font-size: 14px; color: #111; `;
export const PartsGrid = ({ items }) => (
  <PartsGridEl>
    {items.map((it, i) => (
      <PartItem key={i}>
        <ImgBoxSm>
          <img src={it.image} alt={it.label} loading="lazy" onError={(e) => (e.currentTarget.style.display = 'none')} />
        </ImgBoxSm>
        <PartLabel>{it.label}</PartLabel>
      </PartItem>
    ))}
  </PartsGridEl>
);

/* 7) 동영상 블록 */
const VideoBox = styled.div`
  width: 100%; max-width: 100%; aspect-ratio: 16 / 9; background: #000; border: 1px solid #eef1f5;
  video { width: 100%; height: 100%; display: block; object-fit: contain; background: #000; }
`;
export const VideoBlock = ({ src, sources = [], autoPlay = true, loop = true, muted = true }) => {
  const list = src ? [{ src, type: 'video/mp4' }] : sources;
  return (
    <VideoBox>
      <video
        key={(list && list[0] && list[0].src) || 'video'}
        controls playsInline webkit-playsinline="true" x5-playsinline="true" preload="metadata"
        {...(autoPlay ? { autoPlay: true } : {})}
        {...(loop ? { loop: true } : {})}
        {...(muted ? { muted: true } : {})}
      >
        {list.map((s, i) => <source key={i} src={s.src} type={s.type || 'video/mp4'} />)}
        브라우저가 video 태그를 지원하지 않습니다.
      </video>
    </VideoBox>
  );
};

/* 8) 제품 특징 리스트 */
const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 24px;
  max-width: 1400px; 

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureCard = styled.div`
  background: #ffffff;
  border: 1px solid #eef1f5;
  border-radius: 12px;
  padding: 20px 18px 20px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.03);
`;

/* 👉 번호 + 제목 수평 정렬용 헤더 */
const FeatureHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 10px;
`;

const FeatureIndex = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 999px;
  font-size: 15px;
  font-weight: 700;
  color: #ffffff;
  background: #005bac;
  flex-shrink: 0;
`;

const FeatureTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: #111827;
  margin: 0;
  line-height: 1.4;
  padding: 3px 0;
`;

/* 부제목(설명 한 줄) */
const FeatureSubtitle = styled.p`
  margin: 2px 0 0;
  font-size: 13px;
  color: #4b5563;
  line-height: 1.5;
`;

/* 기존 단순 bullet 용 (string 배열용) */
const FeatureBullets = styled.ul`
  margin: 8px 0 0;
  padding-left: 18px;
  font-size: 14px;
  color: #4b5563;
  line-height: 1.6;

  li + li {
    margin-top: 4px;
  }
`;

/* 👉 제목/부제목 밑의 “특징 / 세부특징” 계층 표현용 */
const BulletList = styled.ul`
  margin: 8px 0 0;
  padding-left: 12px;
  list-style: none;
`;

const BulletItem = styled.li`
  & + & {
    margin-top: 8px;
  }
`;

/* • 특징 제목 */
const BulletLabel = styled.div`
  position: relative;
  padding-left: 20px;
  font-size: 14px;
  font-weight: 400;
  color: #111827;
  line-height: 1.5;

  &::before {
    content: '•';
    position: absolute;
    left: 0;
    top: 0;
  }
`;

/*  - 세부 설명 */
const BulletDetail = styled.div`
  position: relative;
  padding-left: 24px;
  margin-top: 2px;
  font-size: 13px;
  color: #4b5563;
  line-height: 1.6;

  &::before {
    content: '-';
    position: absolute;
    left: 10px;
    top: 0;
  }
`;

/**
 * features 데이터 형태:
 * - 심플형: { title, points: ['문장1', '문장2', ...] }
 * - 확장형: { title, subtitle, bullets: [{ label, detail }, ...] }
 */
export const FeatureList = ({ features }) => (
  <FeaturesGrid>
    {features.map((f, idx) => (
      <FeatureCard key={idx}>
        <FeatureHeader>
          <FeatureIndex>{idx + 1}</FeatureIndex>
          <div>
            <FeatureTitle>{f.title}</FeatureTitle>
            {f.subtitle && <FeatureSubtitle>{f.subtitle}</FeatureSubtitle>}
          </div>
        </FeatureHeader>

        {/* 확장형: label + detail 구조 */}
        {f.bullets && (
          <BulletList>
            {f.bullets.map((b, i) => (
              <BulletItem key={i}>
                {/* 문자열만 들어온 경우도 방어적으로 처리 */}
                {typeof b === 'string' ? (
                  <BulletLabel>{b}</BulletLabel>
                ) : (
                  <>
                    {b.label && <BulletLabel>{b.label}</BulletLabel>}
                    {b.detail && <BulletDetail>{b.detail}</BulletDetail>}
                  </>
                )}
              </BulletItem>
            ))}
          </BulletList>
        )}

        {/* 기존 단순 string 배열도 그대로 지원 */}
        {!f.bullets && f.points && (
          <FeatureBullets>
            {f.points.map((p, i) => (
              <li key={i}>{p}</li>
            ))}
          </FeatureBullets>
        )}
      </FeatureCard>
    ))}
  </FeaturesGrid>
);

// --- Type + Usage Showcase ---
const TypeShowcaseWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const TypeShowcaseRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.1fr) minmax(0, 1.4fr);
  gap: 24px;
  align-items: flex-start;
  padding-bottom: 20px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const TypeShowcaseHeader = styled.div`
  margin-bottom: 36px;
`;

// const TypeTag = styled.span`
//   display: inline-flex;
//   align-items: center;
//   justify-content: center;
//   padding: 4px 10px;
//   border-radius: 999px;
//   font-size: 12px;
//   font-weight: 600;
//   letter-spacing: 0.03em;
//   text-transform: uppercase;
//   color: #005bac;
//   background: #e5f1ff;
//   margin-bottom: 6px;
// `;

const TypeShowcaseTitle = styled.h3`
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: #111827;
  line-height: 1.4;
`;

const TypeShowcaseSubtitle = styled.p`
  margin: 4px 0 0;
  font-size: 14px;
  color: #4b5563;
  line-height: 1.5;
`;

/* 왼쪽: 타입 대표 이미지 */
const TypeMain = styled.div`
  width: 100%;
`;

const TypeMainBox = styled(ImgBoxTop)`
  @media (max-width: 900px) {
    height: 320px;
  }
`;

/* 오른쪽: 작동 사진 갤러리 */
const TypeUsageCol = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
`;

const UsageShot = styled.div`
  background: #ffffff;
  padding: 10px 8px 10px;
`;

const UsageShotBox = styled(ImgBoxSm)`
  height: 320px;

  @media (max-width: 1024px) {
    height: 200px;
  }
  @media (max-width: 768px) {
    height: 190px;
  }
`;

const UsageShotCaption = styled.div`
  margin-top: 6px;
  font-size: 13px;
  color: #4b5563;
  text-align: center;
  line-height: 1.4;
`;

/**
 * types 데이터 형태:
 * {
 *   code: 'A',
 *   name: '캐릭터형',
 *   title: 'Type A | 캐릭터형',
 *   subtitle?: '부가 설명',
 *   mainImage: '...png',
 *   shots: [{ image: '...', caption: '...' }, ...]
 * }
 */
export const TypeShowcase = ({ types }) => (
  <TypeShowcaseWrap>
    {types.map((t, idx) => (
      <TypeShowcaseRow key={idx}>
        <TypeMain>
          <TypeShowcaseHeader>
            {/* <TypeTag>{`Type ${t.code}`}</TypeTag> */}
            <TypeShowcaseTitle>{t.title || t.name}</TypeShowcaseTitle>
            {t.subtitle && <TypeShowcaseSubtitle>{t.subtitle}</TypeShowcaseSubtitle>}
          </TypeShowcaseHeader>

          <TypeMainBox>
            <img
              src={t.mainImage}
              alt={t.title || t.name}
              loading="lazy"
              onError={(e) => (e.currentTarget.style.display = 'none')}
            />
          </TypeMainBox>
        </TypeMain>

        <TypeUsageCol>
          {t.shots?.map((shot, i) => (
            <UsageShot key={i}>
              <UsageShotBox>
                <img
                  src={shot.image}
                  alt={shot.caption}
                  loading="lazy"
                  onError={(e) => (e.currentTarget.style.display = 'none')}
                />
              </UsageShotBox>
              {shot.caption && <UsageShotCaption>{shot.caption}</UsageShotCaption>}
            </UsageShot>
          ))}
        </TypeUsageCol>
      </TypeShowcaseRow>
    ))}
  </TypeShowcaseWrap>
);

/* 9) 라벨별 사진 갤러리 */
const InstallGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 30px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const InstallColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
`;

const InstallLabel = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #111827;
  text-align: center;
  margin-bottom: 4px;
`;

const InstallImgBox = styled.div`
  width: 100%;
  max-height: 400px;
  background: #fff;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;  /* 위아래 약간 잘려도 되면 */
    display: block;
  }
`;

/**
 * columns 예시:
 * [
 *   { label: '주간', images: ['...day1.jpg', '...day2.jpg', ...] },
 *   { label: '야간', images: ['...night1.jpg', '...night2.jpg', ...] }
 * ]
 */
export const InstallGallery = ({ columns }) => (
  <InstallGrid>
    {columns.map((col, idx) => (
      <InstallColumn key={idx}>
        {col.label && <InstallLabel>{col.label}</InstallLabel>}

        {col.images?.map((src, i) => (
          <InstallImgBox key={i}>
            <img
              src={src}
              alt={`${col.label || '설치사진'} ${i + 1}`}
              loading="lazy"
              onError={(e) => (e.currentTarget.style.display = 'none')}
            />
          </InstallImgBox>
        ))}
      </InstallColumn>
    ))}
  </InstallGrid>
);


const HighlightWrap = styled.div`
  max-width: 1400px;
  margin: 0 auto 12px;
  padding: 20px 24px 12px;
  border-radius: 16px;
  // border: 1px solid #dbeafe;
  // background: linear-gradient(90deg, #eff6ff, #f9fafb);
  // box-shadow: 0 12px 30px rgba(15, 23, 42, 0.06);
`;

const HighlightEyebrow = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: #1d4ed8;
  letter-spacing: 0.02em;
  margin-bottom: 6px;
`;

const HighlightTitle = styled.h3`
  margin: 0 0 8px;
  font-size: 18px;
  font-weight: 700;
  color: #111827;
  line-height: 1.5;
`;

const HighlightBody = styled.div`
  font-size: 15px;
  line-height: 1.8;
  color: #374151;
  white-space: pre-line; /* \n 유지 */
`;

/**
 * 사용 예시:
 * <UsageHighlight
 *    eyebrow="현장 안전교육 공간으로 활용"
 *    title="현장 안전교육 공간으로 활용"
 *    body={`- ...\n- ...`}
 * />
 */
export const UsageHighlight = ({ eyebrow, title, body }) => (
  <HighlightWrap>
    {eyebrow && <HighlightEyebrow>{eyebrow}</HighlightEyebrow>}
    {title && <HighlightTitle>{title}</HighlightTitle>}
    {body && <HighlightBody>{body}</HighlightBody>}
  </HighlightWrap>
);